// CustomerTabSet.js
import React, { useState, useEffect, createContext, useContext, useRef, useMemo } from 'react';
import { Box, Tabs, Text, Group, Stack } from '@mantine/core';
import CustomerSelect from './CustomerSelect';
import Production from './Production';
import Performance from './Performance';
import { gql, useQuery } from '@apollo/client';
import { GET_PRODUCTION_DAILY } from '../../queries'; // Adjust the import path as needed
import CustomerLandingPage from './CustomerLandingPage';
import CustomerProfile from './CustomerProfile';
import { useSearchParams } from 'react-router-dom';
import CalendarMasterDetail from '../CalendarMasterDetail/CalendarMasterDetail';
import CustomerQuoteDetail from './CustomerQuoteDetail';
import CustomerSalesDetail from './CustomerSalesDetail';
import CustomerCallDetail from './CustomerCallDetail';
import Proficiency from './Proficiency';

// Create a context for sharing customer data
const CustomerDataContext = createContext();

const CustomerTabSet = () => {
  const [activeTab, setActiveTab] = useState('0');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const prevCustomerRef = useRef();
  const [customerData, setCustomerData] = useState(null);
  const initialLoadRef = useRef(true); // To ensure initial selection is handled only once

  const [searchParams, setSearchParams] = useSearchParams();
  const defaultAccountNumber = searchParams.get('acctno');
  const defaultTab = searchParams.get('tab');

  // Define activities configuration for Production
  const activitiesConfig = useMemo(() => [
    {
      type: 'QUOTES',
      color: '#1976d2',
      dataFields: {
        docCount: 'quoteDocCount',
        lineCount: 'quoteLineCount',
        total: 'quoteTotal',
      },
      minimal: false,
      weeklyAverage: true,
      monthlyAverage: true,
      detailView: CustomerQuoteDetail,
    },
    {
      type: 'SALES',
      color: '#2D875D',
      dataFields: {
        docCount: 'salesOrderDocCount',
        lineCount: 'salesOrderLineCount',
        total: 'salesOrderTotal',
      },
      minimal: false,
      weeklyAverage: true,
      monthlyAverage: true,
      detailView: CustomerSalesDetail,
    },
    {
      type: 'CALLS',
      color: '#9C4EE5',
      dataFields: {
        docCount: 'callDocCount',
        lineCount: 'callCount',
        total: 'callTotal',
      },
      minimal: true,
      weeklyAverage: false,
      monthlyAverage: false,
      detailView: CustomerCallDetail,
    },
  ], []);

  // Set the initial active tab based on the 'tab' parameter in the URL
  useEffect(() => {
    if (defaultTab) {
      const tabIndex = parseInt(defaultTab, 10);
      if (!isNaN(tabIndex) && tabIndex >= 0 && tabIndex <= 4) {
        setActiveTab(tabIndex.toString());
      }
    }
  }, [defaultTab]);

  const handleTabChange = (value) => {
    setActiveTab(value);
  };

  const handleCustomerSelect = (customer) => {
    setSelectedCustomer(customer);
    // Optionally, remove 'acctno' from URL if present
    if (searchParams.has('acctno')) {
      searchParams.delete('acctno');
      setSearchParams(searchParams);
    }
  };

  // Execute the query
  const { data, loading, error, refetch } = useQuery(GET_PRODUCTION_DAILY, {
    variables: { acctNo: selectedCustomer?.acctNo || '' },
    skip: !selectedCustomer,
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (selectedCustomer) {
      refetch();
    }
  }, [selectedCustomer, refetch]);

  useEffect(() => {
    if (data && !loading) {
      setCustomerData(data);
    }
  }, [data, loading]);

  // Format the production data as needed for CalendarMasterDetail
  const formattedProductionData = useMemo(() => {
    if (!data) return [];
    return data.dailyProductionForCustomer.map((item) => ({
      date: item.date,
      ...item,
    }));
  }, [data]);

  return (
    <CustomerDataContext.Provider value={{ customerData, loading, error }}>
      <Stack gap={0} h="100%">
        {/* Customer Title and Select */}
        <Group 
          justify="space-between" 
          align="center" 
          px="xs" 
          py={4}
          style={{ borderBottom: '1px solid #e0e0e0' }}
        >
          <Text
            size="xl"
            fw={700}
            c="#FDB813"
            style={{ 
              fontFamily: 'Orbitron, sans-serif',
              fontStyle: 'italic'
            }}
          >
            {selectedCustomer ? `${selectedCustomer.name}` : 'No Customer Selected'}
          </Text>
          <Text size="sm" c="dimmed">
            {selectedCustomer ? `Assigned Salesperson: ${selectedCustomer.assignedSalesperson}` : ''}
          </Text>
          <CustomerSelect onCustomerSelect={handleCustomerSelect} defaultAccountNumber={defaultAccountNumber} />
        </Group>

        {/* Tabs */}
        <Group 
          justify="space-between" 
          align="center"
          style={{ borderBottom: '1px solid var(--mantine-color-gray-3)' }}
          px={0}
          py={0}
        >
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange}
            variant="unstyled"
            styles={{
              root: { flex: 1 },
              list: { gap: 0 },
              tab: {
                padding: '8px 16px',
                color: '#b3b3b3',
                fontWeight: 'normal',
                '&[data-active]': {
                  color: '#ffffff',
                  fontWeight: 'bold',
                  borderBottom: '2px solid white'
                }
              }
            }}
          >
            <Tabs.List>
              <Tabs.Tab value="0">Landing Page</Tabs.Tab>
              <Tabs.Tab value="1">Profile</Tabs.Tab>
              <Tabs.Tab value="2">Production</Tabs.Tab>
              <Tabs.Tab value="3">Performance</Tabs.Tab>
              <Tabs.Tab value="4">Proficiency</Tabs.Tab>
            </Tabs.List>
          </Tabs>

          <Box mr={50}>
            {/* Tab-specific controls can go here if needed */}
          </Box>
        </Group>

        {/* Tab Content */}
        <Box style={{ flexGrow: 1, overflow: 'hidden', height: '100%' }}>
          {activeTab === '0' && (
            <CustomerLandingPage 
              selectedCustomer={selectedCustomer} 
              setActiveTab={setActiveTab} 
            />
          )}
          {activeTab === '1' && (
            <CustomerProfile selectedCustomer={selectedCustomer} />
          )}
          {activeTab === '2' && (
            <>
              {!selectedCustomer ? (
                <Text p="xs">
                  Please select a customer to view production data.
                </Text>
              ) : loading ? (
                <Text p="xs">
                  Loading Production Data...
                </Text>
              ) : error ? (
                <Text p="xs" c="red">
                  Error loading production data.
                </Text>
              ) : data ? (
                <CalendarMasterDetail
                  businessKey={selectedCustomer.acctNo}
                  data={formattedProductionData}
                  currentYear={new Date().getFullYear()}
                  currentMonth={new Date().getMonth()}
                  onMonthChange={(year, month) => {
                    console.log(`Month changed to: ${month + 1}/${year}`);
                  }}
                  activities={activitiesConfig}
                />
              ) : null}
            </>
          )}
          {activeTab === '3' && <Performance selectedCustomer={selectedCustomer} />}
          {activeTab === '4' && <Proficiency selectedCustomer={selectedCustomer} />}
        </Box>
      </Stack>
    </CustomerDataContext.Provider>
  );
};

export default CustomerTabSet;